import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getBrandImage } from '../data/brandImages';
import BenefitCard from './club/BenefitCard';
import VoucherCard from './club/VoucherCard';
import MovementRow from './club/MovementRow';
import { RedeemModals } from './club/RedeemModals';
import useRedeemBenefit from '../hooks/useRedeemBenefit';
import {
  getVoucherStatus,
  getTierForPoints,
  getTiers,
  getRewardTerms,
  nearbyRewards,
  usableRewards,
  withRunningBalance,
} from '../utils/loyalty';

// ── Tier visual config ────────────────────────────────────────────────────────
const TIER_CONFIG = {
  bronce: {
    label: 'Bronce',
    icon: '🥉',
    gradient: 'linear-gradient(135deg, #E30613 0%, #7f0000 100%)',
    accentColor: '#fca5a5',
    badgeBg: 'rgba(255,255,255,0.18)',
    badgeColor: '#fff',
  },
  plata: {
    label: 'Plata',
    icon: '🥈',
    gradient: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
    accentColor: '#cbd5e1',
    badgeBg: 'rgba(255,255,255,0.15)',
    badgeColor: '#e2e8f0',
  },
  oro: {
    label: 'Oro',
    icon: '🥇',
    gradient: 'linear-gradient(135deg, #78350f 0%, #b45309 50%, #f59e0b 100%)',
    accentColor: '#fde68a',
    badgeBg: 'rgba(255,255,255,0.2)',
    badgeColor: '#fff8dc',
  },
};

function TierBadge({ tier }) {
  const cfg = TIER_CONFIG[tier?.key] || TIER_CONFIG.bronce;
  return (
    <span
      style={{ background: cfg.badgeBg, color: cfg.badgeColor, border: '1px solid rgba(255,255,255,0.25)' }}
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wide"
    >
      {cfg.icon} {cfg.label}
    </span>
  );
}

function TierProgress({ tier, puntosAcumulados, tiers }) {
  const ordered = ['bronce', 'plata', 'oro'];
  const idx = ordered.indexOf(tier?.key ?? 'bronce');
  if (idx === ordered.length - 1) {
    return <p className="text-xs text-white/70 mt-3 font-semibold">✨ Nivel máximo — ¡sos parte de la élite Maja!</p>;
  }
  const nextKey  = ordered[idx + 1];
  const nextTier = tiers[nextKey];
  const currMin  = tier?.min ?? 0;
  const nextMin  = Number(nextTier?.min ?? 0);
  const progress = Math.min(100, Math.round(((puntosAcumulados - currMin) / (nextMin - currMin)) * 100));
  const faltan   = Math.max(0, nextMin - puntosAcumulados).toLocaleString('es-AR');
  const nextCfg  = TIER_CONFIG[nextKey];
  return (
    <div className="mt-4">
      <div className="flex justify-between items-center text-xs text-white/70 mb-1.5">
        <span>Camino a <strong className="text-white">{nextCfg.label} {nextCfg.icon}</strong></span>
        <span className="font-black text-white">{progress}%</span>
      </div>
      <div className="h-2 rounded-full bg-white/20 overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
          style={{ width: `${progress}%`, background: nextCfg.accentColor }} />
      </div>
      <p className="text-xs text-white/60 mt-1.5">
        Te faltan <strong className="text-white/90">{faltan} pts</strong> para subir de nivel
      </p>
    </div>
  );
}

function SectionHeader({ title, action, onAction }) {
  return (
    <div className="flex items-end justify-between mb-3">
      <h2 className="text-sm font-black text-gray-500 uppercase tracking-widest">{title}</h2>
      {action && (
        <button onClick={onAction} className="text-xs font-black text-maja-red">{action}</button>
      )}
    </div>
  );
}

function EmptyState({ icon, text }) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <span className="text-3xl mb-2">{icon}</span>
      <p className="text-sm text-gray-400 max-w-xs">{text}</p>
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────
const ClubDashboard = ({ userProfile, setCurrentView, siteConfig, refreshUserProfile }) => {
  const [rewards, setRewards]         = useState([]);
  const [vouchers, setVouchers]       = useState([]);
  const [movimientos, setMovimientos] = useState([]);

  const userName         = userProfile?.nombre || '';
  const userPoints       = Number(userProfile?.puntos || 0);
  const puntosAcumulados = Number(userProfile?.puntosAcumulados ?? userPoints);
  const tiers            = getTiers(siteConfig);
  const tier             = getTierForPoints(puntosAcumulados, siteConfig);
  const tierCfg          = TIER_CONFIG[tier?.key] || TIER_CONFIG.bronce;
  const banner           = siteConfig?.notificacionesActivas ? siteConfig?.mensajeBanner : '';
  const redeem           = useRedeemBenefit({ userPoints, refreshUserProfile });

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(query(collection(db, 'beneficios'), where('activo', '==', true)));
        setRewards(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch { setRewards([]); }
    };
    load();
  }, []);

  useEffect(() => {
    const uid = userProfile?.uid;
    if (!uid) return;
    let cancelled = false;
    const loadExtras = async () => {
      try {
        const vSnap = await getDocs(query(collection(db, 'vouchers'), where('userId', '==', uid)));
        if (!cancelled) setVouchers(vSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch { if (!cancelled) setVouchers([]); }
      try {
        const mSnap = await getDocs(query(collection(db, 'movimientos'), where('clienteId', '==', uid)));
        if (!cancelled) setMovimientos(mSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch { if (!cancelled) setMovimientos([]); }
    };
    loadExtras();
    return () => { cancelled = true; };
  }, [userProfile?.uid]);

  const today          = usableRewards(rewards, userPoints);
  const soon           = nearbyRewards(rewards, userPoints, 3);
  const activeVouchers = vouchers.filter((v) => getVoucherStatus(v) === 'ACTIVO');
  const history        = withRunningBalance(movimientos, userPoints).slice(0, 4);

  return (
    <div className="bg-gray-50 min-h-screen pb-12">

      {/* ── Layout de dos columnas en desktop ── */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 md:grid md:grid-cols-[320px_1fr] md:gap-8 md:items-start">

        {/* ── Banner notificación — script sutil ── */}
        {banner && (
          <p className="md:col-span-2 font-script text-4xl text-maja-red/75 mb-1 tracking-wide text-center">
            {banner}
          </p>
        )}

        {/* ── Hero con foto — dentro del contenedor ── */}
        <div
          className="relative rounded-3xl overflow-hidden mb-4 md:mb-0 md:col-span-2"
          style={{
            backgroundImage: `url(${getBrandImage(siteConfig, 'bannerClub')})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            height: 160,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          <div className="relative z-10 h-full flex flex-col justify-center px-7">
            <p className="text-white/75 text-sm font-semibold">Hola, {userName}</p>
            <h1 className="font-display text-4xl text-white leading-tight">Tu Club Maja</h1>
          </div>
        </div>

        {/* ── Columna izquierda: tarjeta de puntos + nivel ── */}
        <div className="md:sticky md:top-6">

          {/* Tarjeta de membresía */}
          <div
            className="rounded-3xl relative overflow-hidden shadow-lg"
            style={{ background: tierCfg.gradient, aspectRatio: '1.586 / 1' }}
          >
            {/* Círculos decorativos */}
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-10"
              style={{ background: tierCfg.accentColor }} />
            <div className="absolute -left-4 -bottom-8 w-32 h-32 rounded-full opacity-10"
              style={{ background: tierCfg.accentColor }} />

            {/* Chip metálico decorativo */}
            <div className="absolute top-5 right-5 w-10 h-7 rounded-md opacity-40"
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.6), rgba(255,255,255,0.15))', border: '1px solid rgba(255,255,255,0.3)' }} />

            <div className="relative z-10 h-full flex flex-col justify-between p-6">

              {/* Fila superior: logo + nivel */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-black text-base leading-tight tracking-wide">Maja</p>
                  <p className="font-extrabold tracking-widest uppercase text-[10px] leading-tight"
                    style={{ color: tierCfg.accentColor }}>Club</p>
                </div>
                <TierBadge tier={tier} />
              </div>

              {/* Puntos centrales */}
              <div>
                <p className="text-xs font-extrabold uppercase tracking-widest mb-0.5"
                  style={{ color: `${tierCfg.accentColor}99` }}>Puntos disponibles</p>
                <p className="font-black tabular-nums text-white leading-none"
                  style={{ fontSize: 44 }}>
                  {userPoints.toLocaleString('es-AR')}
                </p>
              </div>

              {/* Fila inferior: titular + DNI + ID socio */}
              <div className="flex items-end justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] font-extrabold uppercase tracking-widest mb-0.5"
                    style={{ color: `${tierCfg.accentColor}80` }}>Titular</p>
                  <p className="text-white font-black text-sm truncate uppercase tracking-wider">
                    {((userProfile?.nombre || '') + ' ' + (userProfile?.apellido || '')).trim() || 'Cliente Maja'}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  {userProfile?.dni && (
                    <div className="mb-1">
                      <p className="text-[9px] font-extrabold uppercase tracking-widest"
                        style={{ color: `${tierCfg.accentColor}80` }}>DNI</p>
                      <p className="text-white font-black text-xs tabular-nums tracking-widest">
                        {Number(userProfile.dni).toLocaleString('es-AR')}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-[9px] font-extrabold uppercase tracking-widest"
                      style={{ color: `${tierCfg.accentColor}80` }}>N° Socio</p>
                    <p className="font-black text-xs tabular-nums tracking-widest"
                      style={{ color: tierCfg.accentColor }}>
                      {userProfile?.uid ? userProfile.uid.slice(-8).toUpperCase() : '--------'}
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Barra de progreso separada */}
          <div className="mt-3 rounded-2xl px-5 py-4"
            style={{ background: tierCfg.gradient, opacity: 0.92 }}>
            <TierProgress tier={tier} puntosAcumulados={puntosAcumulados} tiers={tiers} />
          </div>

          {/* Medallero de niveles */}
          <div className="mt-4 rounded-2xl bg-white border border-gray-100 p-4">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4">Niveles del club</p>
            <div className="grid grid-cols-3 gap-2 items-end">
              {[
                {
                  key: 'bronce', min: 0, next: 1000,
                  medal: (
                    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="26" cy="30" r="18" fill="#CD7F32" />
                      <circle cx="26" cy="30" r="14" fill="#E8A060" />
                      <circle cx="26" cy="30" r="10" fill="#CD7F32" />
                      <ellipse cx="21" cy="24" rx="4" ry="2.5" fill="#F0B87A" opacity="0.6" transform="rotate(-30 21 24)" />
                      <rect x="20" y="4" width="12" height="5" rx="2" fill="#A0522D" />
                      <rect x="22" y="8" width="8" height="7" rx="1" fill="#C1692A" />
                      <path d="M22 15 L26 8 L30 15" fill="#C1692A" />
                    </svg>
                  ),
                },
                {
                  key: 'plata', min: 1000, next: 3000,
                  medal: (
                    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="26" cy="30" r="18" fill="#8A8A8A" />
                      <circle cx="26" cy="30" r="14" fill="#C0C0C0" />
                      <circle cx="26" cy="30" r="10" fill="#A8A8A8" />
                      <ellipse cx="21" cy="24" rx="4" ry="2.5" fill="#E8E8E8" opacity="0.7" transform="rotate(-30 21 24)" />
                      <rect x="20" y="4" width="12" height="5" rx="2" fill="#6A6A6A" />
                      <rect x="22" y="8" width="8" height="7" rx="1" fill="#8A8A8A" />
                      <path d="M22 15 L26 8 L30 15" fill="#8A8A8A" />
                    </svg>
                  ),
                },
                {
                  key: 'oro', min: 3000, next: null,
                  medal: (
                    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="26" cy="30" r="18" fill="#B8860B" />
                      <circle cx="26" cy="30" r="14" fill="#FFD700" />
                      <circle cx="26" cy="30" r="10" fill="#DAA520" />
                      <ellipse cx="21" cy="24" rx="4" ry="2.5" fill="#FFFACD" opacity="0.7" transform="rotate(-30 21 24)" />
                      <rect x="20" y="4" width="12" height="5" rx="2" fill="#8B6914" />
                      <rect x="22" y="8" width="8" height="7" rx="1" fill="#B8860B" />
                      <path d="M22 15 L26 8 L30 15" fill="#B8860B" />
                    </svg>
                  ),
                },
              ].map(({ key, min, next, medal }) => {
                const cfg    = TIER_CONFIG[key];
                const active = tier?.key === key;
                const done   = tier?.key === 'plata' && key === 'bronce'
                             || tier?.key === 'oro';
                const isDone = key === 'bronce' && (tier?.key === 'plata' || tier?.key === 'oro')
                             || key === 'plata' && tier?.key === 'oro';
                return (
                  <div
                    key={key}
                    className="flex flex-col items-center rounded-2xl p-3 transition-all duration-300"
                    style={{
                      transform: active ? 'translateY(-8px)' : 'translateY(0)',
                      background: active ? 'rgba(185,28,28,0.05)' : 'transparent',
                      border: active ? '1.5px solid rgba(185,28,28,0.25)' : '1.5px solid transparent',
                      boxShadow: active ? '0 8px 24px rgba(185,28,28,0.12)' : 'none',
                      opacity: !active && !isDone ? 0.45 : 1,
                    }}
                  >
                    {active && (
                      <span className="text-[9px] font-black uppercase tracking-widest text-maja-red mb-1">tu nivel</span>
                    )}
                    {isDone && (
                      <span className="text-[9px] font-black uppercase tracking-widest text-emerald-600 mb-1">superado</span>
                    )}
                    {!active && !isDone && (
                      <span className="text-[9px] font-black uppercase tracking-widest text-gray-300 mb-1">bloqueado</span>
                    )}
                    <div style={{ filter: isDone ? 'saturate(0.6)' : active ? 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' : 'saturate(0.3) brightness(0.9)' }}>
                      {medal}
                    </div>
                    <p className={`mt-2 text-xs font-black ${active ? 'text-gray-800' : isDone ? 'text-gray-500' : 'text-gray-300'}`}>
                      {cfg.label}
                    </p>
                    <p className="text-[10px] text-gray-400 text-center mt-0.5">
                      {next
                        ? `${min.toLocaleString('es-AR')} – ${next.toLocaleString('es-AR')}`
                        : `${min.toLocaleString('es-AR')}+`}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* ── Columna derecha: beneficios ── */}
        <div>

          {/* Disponibles ahora */}
          {today.length > 0 && (
            <div className="mb-8">
              <SectionHeader title="Disponibles ahora" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {today.map((reward, i) => (
                  <BenefitCard
                    key={reward.id}
                    reward={reward}
                    index={i}
                    cta="Usar beneficio"
                    onAction={() => redeem.start(reward)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Todos los beneficios */}
          <div className="mb-8">
            <SectionHeader title="Todos los beneficios" />
            {rewards.length === 0 ? (
              <EmptyState icon="🎁" text="Pronto vas a encontrar beneficios acá." />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {rewards
                  .slice()
                  .sort((a, b) => getRewardTerms(a).puntos - getRewardTerms(b).puntos)
                  .map((reward, i) => {
                    const isUsable = today.some(r => r.id === reward.id)
                    const faltan = isUsable ? 0 : Math.max(0, getRewardTerms(reward).puntos - userPoints)
                    return (
                      <BenefitCard
                        key={reward.id}
                        reward={reward}
                        index={i}
                        faltan={isUsable ? undefined : faltan}
                        cta={isUsable ? 'Usar beneficio' : 'Ver canjes'}
                        onAction={() => isUsable ? redeem.start(reward) : setCurrentView('canjes')}
                      />
                    )
                  })}
              </div>
            )}
          </div>


        </div>
      </div>

      <RedeemModals {...redeem} />
    </div>
  );
};

export default ClubDashboard;
